import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { DollarSign, Upload, CheckCircle2, AlertTriangle, Save, Trash2, Plus } from "lucide-react";

export default function SpendPanel({ contract }) {
  const [schedule, setSchedule] = useState({ years: [], auto_suggested: false });
  const [poinvoices, setPOInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [uploadKind, setUploadKind] = useState("invoice");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!contract?.id) return;
    try {
      const [s, p, sm] = await Promise.all([
        api.get(`/contracts/${contract.id}/schedule`),
        api.get(`/contracts/${contract.id}/po-invoices`),
        api.get(`/contracts/${contract.id}/spend-summary`),
      ]);
      setSchedule(s.data);
      setPOInvoices(p.data || []);
      setSummary(sm.data);
    } catch (e) { toast.error("Load failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [contract?.id]);

  const saveSchedule = async () => {
    setBusy(true);
    try {
      await api.post(`/contracts/${contract.id}/schedule`, { years: schedule.years });
      toast.success("Schedule saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const addYear = () => {
    const last = schedule.years[schedule.years.length - 1];
    const y = last ? last.year + 1 : 1;
    setSchedule({ ...schedule, years: [...schedule.years, {
      year: y, start_date: "", end_date: "", contracted_amount: 0, cpi_percentage: 0, notes: "",
    }]});
  };

  const removeYear = (i) => {
    const next = schedule.years.filter((_, idx) => idx !== i);
    setSchedule({ ...schedule, years: next });
  };

  const onFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", uploadKind);
      await api.post(`/contracts/${contract.id}/po-invoices/upload`, fd,
                     { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      toast.success(`${uploadKind.toUpperCase()} uploaded — AI extraction pending review`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const confirmPOI = async (poi) => {
    try {
      await api.patch(`/po-invoices/${poi.id}/confirm`, {
        po_number: poi.po_number, invoice_number: poi.invoice_number,
        vendor: poi.vendor, amount: parseFloat(poi.amount) || 0,
        invoice_date: poi.invoice_date, po_date: poi.po_date,
        service_period_start: poi.service_period_start,
        service_period_end: poi.service_period_end,
        contract_year: poi.contract_year ? parseInt(poi.contract_year) : null,
      });
      toast.success("Confirmed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const delPOI = async (id) => {
    if (!confirm("Delete this record?")) return;
    try { await api.delete(`/po-invoices/${id}`); load(); } catch { toast.error("Failed"); }
  };

  const updatePOIField = (id, field, value) => {
    setPOInvoices(poinvoices.map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  return (
    <div className="space-y-4" data-testid="spend-panel">
      {/* Schedule */}
      <div className="bg-white border border-slate-200 rounded-md">
        <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 rounded-t-md flex items-center justify-between">
          <div className="flex items-center gap-2 font-semibold text-sm">
            <DollarSign className="w-4 h-4 text-[#005289]" /> Contract Year Schedule
            {schedule.auto_suggested && <span className="text-[10px] text-amber-700 bg-amber-100 px-2 py-0.5 rounded">Auto-suggested</span>}
          </div>
          <div className="flex gap-2">
            <button className="text-xs px-2 py-1 border border-slate-300 rounded" onClick={addYear} data-testid="schedule-add-year">
              <Plus className="w-3 h-3 inline" /> Add year
            </button>
            <button disabled={busy} className="text-xs px-2 py-1 bg-[#005289] text-white rounded disabled:opacity-50" onClick={saveSchedule} data-testid="schedule-save">
              <Save className="w-3 h-3 inline mr-1" /> Save schedule
            </button>
          </div>
        </div>
        <div className="p-3 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-1">Year</th>
                <th className="text-left p-1">Start</th>
                <th className="text-left p-1">End</th>
                <th className="text-left p-1">Contracted $</th>
                <th className="text-left p-1">CPI %</th>
                <th className="text-left p-1">Notes</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {schedule.years.map((y, i) => (
                <tr key={i} data-testid={`schedule-row-${y.year}`}>
                  <td className="p-1"><input type="number" className="w-14 h-7 px-1 border border-slate-200 rounded" value={y.year} onChange={e => { const n = [...schedule.years]; n[i].year = parseInt(e.target.value) || 0; setSchedule({...schedule, years: n}); }} /></td>
                  <td className="p-1"><input type="date" className="h-7 px-1 border border-slate-200 rounded" value={y.start_date || ""} onChange={e => { const n = [...schedule.years]; n[i].start_date = e.target.value; setSchedule({...schedule, years: n}); }} data-testid={`schedule-start-${y.year}`} /></td>
                  <td className="p-1"><input type="date" className="h-7 px-1 border border-slate-200 rounded" value={y.end_date || ""} onChange={e => { const n = [...schedule.years]; n[i].end_date = e.target.value; setSchedule({...schedule, years: n}); }} data-testid={`schedule-end-${y.year}`} /></td>
                  <td className="p-1"><input type="number" className="w-28 h-7 px-1 border border-slate-200 rounded" value={y.contracted_amount || 0} onChange={e => { const n = [...schedule.years]; n[i].contracted_amount = parseFloat(e.target.value) || 0; setSchedule({...schedule, years: n}); }} data-testid={`schedule-amount-${y.year}`} /></td>
                  <td className="p-1"><input type="number" step="0.01" className="w-16 h-7 px-1 border border-slate-200 rounded" value={y.cpi_percentage || 0} onChange={e => { const n = [...schedule.years]; n[i].cpi_percentage = parseFloat(e.target.value) || 0; setSchedule({...schedule, years: n}); }} data-testid={`schedule-cpi-${y.year}`} /></td>
                  <td className="p-1"><input className="w-32 h-7 px-1 border border-slate-200 rounded" value={y.notes || ""} onChange={e => { const n = [...schedule.years]; n[i].notes = e.target.value; setSchedule({...schedule, years: n}); }} /></td>
                  <td className="p-1"><button className="text-red-600 hover:bg-red-50 p-1 rounded" onClick={() => removeYear(i)}><Trash2 className="w-3.5 h-3.5" /></button></td>
                </tr>
              ))}
              {schedule.years.length === 0 && <tr><td colSpan={7} className="p-4 text-center text-slate-400">No schedule yet. Add contract years.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upload PO / Invoice */}
      <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="poi-upload">
        <div className="text-sm font-semibold mb-3">Attach PO or Invoice</div>
        <div className="flex items-center gap-3">
          <select className="h-9 px-3 text-sm border border-slate-200 rounded" value={uploadKind} onChange={e => setUploadKind(e.target.value)} data-testid="poi-kind">
            <option value="invoice">Invoice</option>
            <option value="po">Purchase Order</option>
          </select>
          <input type="file" accept=".pdf,.docx,.png,.jpg,.jpeg" onChange={onFile} disabled={busy} data-testid="poi-file" />
          <div className="text-xs text-slate-500">AI extracts vendor / amount / dates. You confirm.</div>
        </div>
      </div>

      {/* Spend vs Contract table */}
      {summary && (
        <div className="bg-white border border-slate-200 rounded-md" data-testid="spend-summary">
          <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 rounded-t-md flex items-center justify-between">
            <div className="font-semibold text-sm">Spend vs Contracted (by year)</div>
            <div className="text-xs text-slate-600">
              Total Contracted <b>${(summary.total_contracted || 0).toLocaleString()}</b> ·
              Actual <b>${(summary.total_actual || 0).toLocaleString()}</b> ·
              Var <b className={summary.total_variance > 0 ? "text-red-700" : "text-emerald-700"}>${summary.total_variance.toLocaleString()}</b>
            </div>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-2">Year</th>
                <th className="text-left p-2">Period</th>
                <th className="text-left p-2">Contracted</th>
                <th className="text-left p-2">Actual</th>
                <th className="text-left p-2">Variance</th>
                <th className="text-left p-2"># Inv</th>
                <th className="text-left p-2">Flags</th>
              </tr>
            </thead>
            <tbody>
              {summary.years.map(y => (
                <tr key={y.year} className="border-t border-slate-100" data-testid={`spend-year-${y.year}`}>
                  <td className="p-2 font-semibold">Y{y.year}</td>
                  <td className="p-2 text-slate-500">{y.start_date} → {y.end_date}</td>
                  <td className="p-2">${(y.contracted || 0).toLocaleString()}</td>
                  <td className="p-2">${(y.actual || 0).toLocaleString()}</td>
                  <td className={`p-2 font-semibold ${y.variance > 0 ? "text-red-700" : "text-emerald-700"}`}>${y.variance.toLocaleString()} ({y.variance_pct}%)</td>
                  <td className="p-2">{y.invoice_count}</td>
                  <td className="p-2">
                    {y.flags.length === 0 ? <span className="text-slate-400">—</span> :
                      y.flags.map((f, i) => (
                        <div key={i} className={`text-[10px] px-1.5 py-0.5 rounded mb-0.5 ${f.severity === "high" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`} data-testid={`spend-flag-${y.year}-${f.type}`}>
                          <AlertTriangle className="w-3 h-3 inline mr-1" /> {f.message}
                        </div>
                      ))
                    }
                  </td>
                </tr>
              ))}
              {summary.years.length === 0 && <tr><td colSpan={7} className="p-4 text-center text-slate-400">Set the schedule to see spend analysis.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* PO/Invoices list */}
      <div className="bg-white border border-slate-200 rounded-md" data-testid="poi-list">
        <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 rounded-t-md font-semibold text-sm">POs & Invoices ({poinvoices.length})</div>
        {poinvoices.length === 0 ? (
          <div className="p-6 text-sm text-slate-500 text-center">No POs or invoices attached yet.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-2">Kind</th>
                <th className="text-left p-2">#</th>
                <th className="text-left p-2">Vendor</th>
                <th className="text-left p-2">Amount</th>
                <th className="text-left p-2">Date</th>
                <th className="text-left p-2">Year</th>
                <th className="text-left p-2">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {poinvoices.map(p => (
                <tr key={p.id} className="border-t border-slate-100" data-testid={`poi-row-${p.id}`}>
                  <td className="p-2 uppercase font-semibold text-[10px]">{p.kind}</td>
                  <td className="p-2"><input className="w-24 h-6 px-1 border border-slate-200 rounded" value={p.kind === "po" ? (p.po_number || "") : (p.invoice_number || "")} onChange={e => updatePOIField(p.id, p.kind === "po" ? "po_number" : "invoice_number", e.target.value)} /></td>
                  <td className="p-2"><input className="w-32 h-6 px-1 border border-slate-200 rounded" value={p.vendor || ""} onChange={e => updatePOIField(p.id, "vendor", e.target.value)} /></td>
                  <td className="p-2"><input type="number" className="w-24 h-6 px-1 border border-slate-200 rounded" value={p.amount || 0} onChange={e => updatePOIField(p.id, "amount", e.target.value)} data-testid={`poi-amount-${p.id}`} /></td>
                  <td className="p-2"><input type="date" className="h-6 px-1 border border-slate-200 rounded" value={(p.kind === "invoice" ? p.invoice_date : p.po_date) || ""} onChange={e => updatePOIField(p.id, p.kind === "invoice" ? "invoice_date" : "po_date", e.target.value)} data-testid={`poi-date-${p.id}`} /></td>
                  <td className="p-2"><input type="number" className="w-14 h-6 px-1 border border-slate-200 rounded" value={p.contract_year || ""} onChange={e => updatePOIField(p.id, "contract_year", e.target.value)} /></td>
                  <td className="p-2">
                    {p.confirmed ? <span className="text-emerald-700 text-[10px] flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Confirmed</span>
                                 : <span className="text-amber-700 text-[10px]">Unconfirmed</span>}
                  </td>
                  <td className="p-2 whitespace-nowrap">
                    <button className="text-xs px-2 py-0.5 bg-emerald-600 text-white rounded mr-1" onClick={() => confirmPOI(p)} data-testid={`poi-confirm-${p.id}`}>Confirm</button>
                    <button className="text-xs p-1 text-red-600 hover:bg-red-50 rounded" onClick={() => delPOI(p.id)}><Trash2 className="w-3 h-3" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
